import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, OnChanges, ViewChild } from '@angular/core';
import { SourceComment } from '@components/main/spreading/models/source-comment';
import { FormatValuePipe } from '../../../pipes/format-value.pipe';
import { LocaleNumberPipe } from '../../../pipes/locale-number.pipe';


@Component({
    selector: 'app-double-click-to-edit',
    templateUrl: './double-click-to-edit.component.html',
    styleUrls: ['./double-click-to-edit.component.scss'],
    standalone: false,
    providers: [FormatValuePipe, LocaleNumberPipe]
})
export class DoubleClickToEditComponent implements OnInit, OnChanges {
  @Input() value = '';
  @Input() sourceComments: Array<SourceComment>;
  @Input() isEditingDisabled: boolean = false;
  @Output() changed = new EventEmitter<string>();
  @Output() enteredEditMode = new EventEmitter<boolean>();
  @Output() valueBeforeEditing = new EventEmitter<string>
  @Input() editing = false;
  @Input() alignRight = false;
  @Input() formatAsCurrency = false;
  @Input() hasPadding = false;
  @Input() invertValue?: boolean = false;
  @Input() footnotePresent?: boolean = false;
  @Input() isUserAddedRow?: boolean = false;
  @Input() textFormat?: string = '';
  @Input() isStatementColumn?: boolean = false;
  @Input() isOffsetLinkRow?: boolean = false;
  @Input() offsetLinkTooltip?: string = '';
  @Input() placeholder?: string = '';
  currentValue = '';
  originalValue: string = '';
  formattedValue: string = '';

  @ViewChild('inputField') inputField: any;

  constructor(
    private formatValuePipe: FormatValuePipe,
    private localeNumberPipe: LocaleNumberPipe
  ) { }

  ngOnInit() {
    if (this.formatAsCurrency && !/^-?\d+(\.\d+)?$/.test(this.value)) {
      this.formatAsCurrency = false;
    }
    this.computeFormattedValue();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['value'] || changes['textFormat'] || changes['formatAsCurrency']) {
      this.computeFormattedValue();
    }
    if (changes.hasOwnProperty('value') && !this.editing) {
      this.currentValue = changes['value'].currentValue;
    }
    if (changes.hasOwnProperty('editing') && !changes.editing.firstChange) {
      if (changes['editing'].currentValue) {
        this.originalValue = this.currentValue;
        if (this.isStatementColumn) {
          const numValue = parseFloat(this.currentValue);
          if (!isNaN(numValue) && numValue === 0) {
            this.currentValue = '';
          }
        }
        setTimeout(() => {
          if (this.inputField && this.inputField.nativeElement) {
            this.inputField.nativeElement.focus();
          }
        });
      } else {
        if (changes['editing'].currentValue !== changes['editing'].previousValue) {
          if (this.isStatementColumn) {
            const trimmed = (this.currentValue ?? '').toString().trim();
            if (trimmed === '') {
              const numericOriginal = parseFloat(this.originalValue);
              if (!isNaN(numericOriginal) && numericOriginal === 0) {
                this.currentValue = this.originalValue || '0';
              } else {
                this.currentValue = '0';
              }
            }
          }
          this.changed.emit(this.currentValue);
        }
      }
    }
  }

  startEditing(evt) {
    if (this.isEditingDisabled) {
      return
    }
    this.stopPropagation(evt);
    this.enteredEditMode.emit(true);
    this.valueBeforeEditing.emit(this.value)
  }

  stopPropagation(evt) {
    evt.preventDefault();
    evt.stopPropagation();
  }

  checkForEnter(evt) {
    if (evt?.keyCode === 13) {
      this.originalValue = this.currentValue;
      this.stopPropagation(evt);
      this.changed.emit(this.currentValue);
    }

    if (evt?.key === 'Escape') {
      this.currentValue = this.originalValue;
      this.stopPropagation(evt);
      this.changed.emit(this.currentValue);
    }
  }

  private computeFormattedValue(): void {
    const formatted = this.localeNumberPipe.transform(
      this.formatValuePipe.transform(this.value, this.textFormat, this.formatAsCurrency),
      '1.2-2'
    );
    this.formattedValue = formatted == null ? '' : String(formatted);
  }
}
